/// <amd-module name="Repos/SparseTree/SparseTreeProvider" />
import {
    ISparseTreeProvider,
    ISparseTreeProviderFetchResult,
    SparseTreeFolder,
    SparseTreeItem,
    SparseTreeNode
} from "Repos/SparseTree/SparseTreeProvider.Types";
import { addToPartitionedSortedArray, getInsertIndexOfPartitionSortedTreeItems } from "Utils";
import { IObservableLikeValue, ObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { IObservable, IObservableArrayEventArgs, ObservableArrayAction } from "VSS/Core/Observable";
import { IItemProvider } from "VSSUI/Utilities/Provider";
import { ITreeItem, ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";

interface SparseTreeSearchResult<T> {
    item: ITreeItemEx<SparseTreeNode<T>> | undefined;
    commonIdentifier: string;
}

interface SparseTreeAddResult<T> {
    spliceIndex: number;
    removeCount: number;
    itemsToAdd: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>[];
    newItemAncestry: ITreeItemEx<SparseTreeNode<T>>[];
}

export class SparseTreeProvider<T>
    implements
        ISparseTreeProvider<T>,
        IItemProvider<ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>>,
        IObservable<IObservableArrayEventArgs<ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>>, ObservableArrayAction> {
    public get length(): number {
        return this.tableItems.length;
    }

    public get value(): ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>[] {
        return this.tableItems.value;
    }

    // Fields concerned with async behavior
    private onFetchChildren?: (
        treeItem: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T>>>,
        page: number
    ) => Promise<ISparseTreeProviderFetchResult<T>>;
    private mapItemsToPages: { [identifier: string]: number } = {};
    private mapItemsToHasMore: { [identifier: string]: boolean } = {};

    // Fields for tracking contents
    protected readonly containedIdentifiers = new Set<string>();
    protected folders: ITreeItemEx<SparseTreeNode<T>>[] = [];

    // Path construction
    protected readonly getIdentifier: (item: T) => string;
    protected readonly separator: string;

    // Set of items that are given to the underlying table
    protected readonly tableItems = new ObservableArray<ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>>([]);

    /**
     *
     * @param items Items to initialize with
     * @param getIdentifier Function that grabs the logical path of the items
     * @param separator Separator string that will be used to split the path from getIdentifier
     * @param initialVisibleItems Defines the cutoff for expanding items initially - will attempt to show at least n and will not show more than 2n; if it's 0, we'll only display root items. Defaults to 0. If onFetchChildren is provided, this field is ignored
     * @param onFetchChildren Async fetch behavior for expand/collapse/paging of folder data
     */
    constructor(
        items: T[],
        getIdentifier: (item: T) => string,
        separator: string,
        initialVisibleItems: number = 0,
        loadingRows?: number,
        onFetchChildren?: (treeItem: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T>>>, page: number) => Promise<ISparseTreeProviderFetchResult<T>>
    ) {
        this.getIdentifier = getIdentifier;
        this.separator = separator;

        this.initialize(items, initialVisibleItems, onFetchChildren);
        loadingRows && this.tableItems.push(...new Array(loadingRows).fill(this.convertToExItem(this.createLoadingRow())));
    }

    public add = (item: T, expandedImmediately: boolean = true) => {
        const addResult = this.addInternal(item);

        if (addResult) {
            if (addResult.spliceIndex !== -1) {
                this.tableItems.splice(addResult.spliceIndex, addResult.removeCount, ...addResult.itemsToAdd);
            }

            if (expandedImmediately) {
                addResult.newItemAncestry.forEach(x => this.expand(x));
            }
        }
    };

    public initialize = (
        items: T[],
        initialVisibleItems: number,
        onFetchChildren?: (treeItem: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T>>>, page: number) => Promise<ISparseTreeProviderFetchResult<T>>
    ) => {
        // Reset internal tracking structures
        this.folders = [];
        this.containedIdentifiers.clear();
        this.mapItemsToPages = {};
        this.mapItemsToHasMore = {};

        // Initialize new props
        this.onFetchChildren = onFetchChildren;

        // Create new list here instead of on treeItems
        const resultItems: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>[] = [];
        items.forEach(item => {
            const insertResult = this.addInternal(item, resultItems);
            if (insertResult && insertResult.spliceIndex > -1) {
                resultItems.splice(insertResult.spliceIndex, insertResult.removeCount, ...insertResult.itemsToAdd);
            }
        });
        this.tableItems.splice(0, this.tableItems.length, ...resultItems);

        // If we're fetching async, we can't tell how many children a row will have until it resolves
        // As a result, just don't try to expand at all
        if (!this.onFetchChildren) {
            // Expand to the correct number
            let currentIndex = 0;
            while (currentIndex < this.value.length && this.value.length < initialVisibleItems) {
                const currentItem = this.value[currentIndex];
                const dataAtCurrentIndex = ObservableLike.getValue(currentItem.underlyingItem.data);
                const hasValidNumberOfChildren =
                    currentItem.underlyingItem.childItems &&
                    this.value.length + currentItem.underlyingItem.childItems.length < 2 * initialVisibleItems;
                if (dataAtCurrentIndex && dataAtCurrentIndex.kind === "folder" && hasValidNumberOfChildren) {
                    // Checks above make this safe
                    this.expand(currentItem as ITreeItemEx<IObservableLikeValue<SparseTreeNode<T>>>);
                }
                currentIndex += 1;
            }
        }
    };

    public loadNextPage = (treeItem: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T>>>) => {
        if (this.onFetchChildren) {
            const itemData = ObservableLike.getValue(treeItem.underlyingItem.data);
            const itemIdentifier = itemData.absoluteIdentifier;
            const itemHasMoreData = this.mapItemsToHasMore[itemIdentifier] === undefined ? true : this.mapItemsToHasMore[itemIdentifier];

            if (itemHasMoreData) {
                const pageToFetch = this.mapItemsToPages[itemIdentifier] || 1;
                this.onFetchChildren(treeItem, pageToFetch).then(result => {
                    this.mapItemsToPages[itemIdentifier] = pageToFetch + 1;
                    this.mapItemsToHasMore[itemIdentifier] = result.hasMore;

                    this.clearLoadingRows(treeItem.underlyingItem);

                    this.updateVisibleLineageForFolder(treeItem as ITreeItemEx<SparseTreeFolder>, result.data);

                    if (result.hasMore) {
                        this.pushChild(treeItem.underlyingItem, this.createLoadingRow());
                    }
                });
            }
        }
    };

    public modifyItems = (visitor: (item: T) => T) => {
        // Update items in the folders structure
        this.folders.forEach(x => this.visitContainedItems(x.underlyingItem, visitor));

        // Change the actual tree items
        const newItems: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>[] = [];
        this.tableItems.value.forEach(treeItem => {
            const newItem = { ...treeItem };
            const itemData = ObservableLike.getValue(newItem.underlyingItem.data);

            if (!itemData || itemData.kind === "folder") {
                newItems.push(newItem);
            } else {
                itemData.data = visitor(itemData.data);
                newItems.push(newItem);
            }
        });

        this.tableItems.change(0, ...newItems);
    };

    public remove = (item: T) => {
        const absoluteIdentifier = this.getIdentifier(item);
        const parentNodeIdentifier = this.getParentIdentifier(absoluteIdentifier);

        // We'll build up a set so removal is a faster process
        const identifiersToRemoveFromTable = new Set([absoluteIdentifier]);

        // Parent will always be a folder
        let parentNode = this.folders.find(x => x.underlyingItem.data.absoluteIdentifier === parentNodeIdentifier);

        if (parentNode && parentNode.underlyingItem.childItems) {
            // Remove node from parent
            parentNode.underlyingItem.childItems = parentNode.underlyingItem.childItems.filter(x => x.data.absoluteIdentifier !== absoluteIdentifier);

            // Walk up the tree, removing parents that no longer have children
            while (parentNode && parentNode.underlyingItem.childItems && parentNode.underlyingItem.childItems.length === 0) {
                const parentNodeIdentifier = parentNode.underlyingItem.data.absoluteIdentifier;

                // Remove node from folders
                this.folders = this.folders.filter(x => x.underlyingItem.data.absoluteIdentifier !== parentNodeIdentifier);

                // Remove node from its parent
                if (parentNode.parentItem && parentNode.parentItem.underlyingItem.childItems) {
                    parentNode.parentItem.underlyingItem.childItems = parentNode.parentItem.underlyingItem.childItems.filter(
                        x => x.data.absoluteIdentifier !== parentNodeIdentifier
                    );
                }

                identifiersToRemoveFromTable.add(parentNodeIdentifier);

                // One step up
                parentNode = parentNode.parentItem;
            }
        }

        this.tableItems.removeAll(testItem => {
            const testItemData = ObservableLike.getValue(testItem.underlyingItem.data);
            return testItemData ? identifiersToRemoveFromTable.has(testItemData.absoluteIdentifier) : false;
        });

        // Merge folder up if there's only one child folder and there's another parent above this one
        if (
            parentNode &&
            parentNode.underlyingItem.childItems &&
            parentNode.underlyingItem.childItems.length == 1 &&
            parentNode.underlyingItem.childItems[0].data.kind === "folder"
        ) {
            const childNode = parentNode.underlyingItem.childItems[0];
            parentNode.underlyingItem.data.absoluteIdentifier += this.separator + childNode.data.relativeIdentifier;
            parentNode.underlyingItem.data.relativeIdentifier += this.separator + childNode.data.relativeIdentifier;
            parentNode.underlyingItem.childItems = childNode.childItems;
            parentNode.underlyingItem.expanded = childNode.expanded;

            const parentDisplayIndex = this.tableItems.value.indexOf(parentNode);
            const itemsToSplice = [parentNode];

            if (childNode.expanded && childNode.childItems) {
                itemsToSplice.push(...childNode.childItems.map(item => this.convertToExItem(item, parentNode)));
            }

            this.tableItems.splice(parentDisplayIndex, itemsToSplice.length + 1, ...itemsToSplice);
        }

        this.containedIdentifiers.delete(absoluteIdentifier);
    };

    public subscribe(
        observer: (value: IObservableArrayEventArgs<ITreeItemEx<SparseTreeNode<T>>>, action?: ObservableArrayAction) => void,
        action?: ObservableArrayAction
    ): (value: IObservableArrayEventArgs<ITreeItemEx<SparseTreeNode<T>>>, action?: ObservableArrayAction) => void {
        return this.tableItems.subscribe(observer, action);
    }

    public toggle = (event: unknown, item: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>) => {
        const itemData = ObservableLike.getValue(item.underlyingItem.data);

        // Makes the below casts safe
        if (!itemData) {
            return;
        }

        if (item.underlyingItem.expanded) {
            this.collapse(item as ITreeItemEx<IObservableLikeValue<SparseTreeNode<T>>>);
        } else {
            this.expand(item as ITreeItemEx<IObservableLikeValue<SparseTreeNode<T>>>);
        }
    };

    public unsubscribe(
        observer: (value: IObservableArrayEventArgs<ITreeItemEx<SparseTreeNode<T>>>, action?: ObservableArrayAction) => void,
        action?: ObservableArrayAction
    ): void {
        return this.tableItems.unsubscribe(observer, action);
    }

    public update(item: T) {
        const itemIdentifier = this.getIdentifier(item);

        // Look through folders and update the backing struct
        let itemIndexInParent = -1;
        const itemParent = this.folders.find(testItem => {
            if (testItem.underlyingItem.childItems) {
                itemIndexInParent = testItem.underlyingItem.childItems.findIndex(x => x.data.absoluteIdentifier === itemIdentifier);
                return itemIndexInParent !== -1;
            }
            return false;
        });

        const parentNodeIdentifier = itemParent ? itemParent.underlyingItem.data.absoluteIdentifier : "";
        const itemSparseNode: SparseTreeItem<T> = {
            kind: "item",
            absoluteIdentifier: itemIdentifier,
            data: item,
            relativeIdentifier:
                parentNodeIdentifier.length > 0 ? itemIdentifier.substring(parentNodeIdentifier.length + this.separator.length) : itemIdentifier
        };
        const itemTreeNode: ITreeItem<SparseTreeItem<T>> = { data: itemSparseNode };

        // If this is set, index is guaranteed > -1 and children are guaranteed to exist
        if (itemParent) {
            itemParent.underlyingItem.childItems![itemIndexInParent] = itemTreeNode;
        }

        // Update the actual table items
        const positionInTable = this.tableItems.value.findIndex(x => {
            const data = ObservableLike.getValue(x.underlyingItem.data);
            return data && data.absoluteIdentifier === itemIdentifier;
        });

        if (positionInTable > -1) {
            this.tableItems.change(positionInTable, this.convertToExItem(itemTreeNode, itemParent));
        }
    }

    private addChildToInternalStructures = (parentItem: ITreeItem<SparseTreeNode<T>>, childItem: ITreeItem<SparseTreeNode<T>>) => {
        if (parentItem.childItems) {
            addToPartitionedSortedArray(parentItem.childItems, childItem);
        } else {
            parentItem.childItems = [childItem];
        }
    };

    /**
     * @param arrayToSearch used for custom searching in Initialize; defaults to this.tableItems.value
     */
    private addInternal: (
        item: T,
        arrayToSearch?: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>[]
    ) => SparseTreeAddResult<T> | undefined = (item: T, arrayToSearch?: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>[]) => {
        arrayToSearch = arrayToSearch || this.tableItems.value;

        const childAbsoluteIdentifier = this.getIdentifier(item);
        if (this.containedIdentifiers.has(childAbsoluteIdentifier)) {
            return undefined;
        } else {
            this.containedIdentifiers.add(childAbsoluteIdentifier);
        }

        const desiredParentIdentifier = this.getParentIdentifier(childAbsoluteIdentifier);
        const searchResult = this.findClosestAncestor(childAbsoluteIdentifier);
        let parentFolderEx = searchResult.item;
        let parentFolder = parentFolderEx && parentFolderEx.underlyingItem;
        const searchCommonIdentifier = searchResult.commonIdentifier;

        const childSparseNode: SparseTreeItem<T> = {
            kind: "item",
            absoluteIdentifier: childAbsoluteIdentifier,
            data: item,
            relativeIdentifier:
                desiredParentIdentifier.length > 0
                    ? childAbsoluteIdentifier.substring(desiredParentIdentifier.length + this.separator.length)
                    : childAbsoluteIdentifier
        };
        const childTreeNode: ITreeItem<SparseTreeItem<T>> = { data: childSparseNode };

        if (parentFolderEx && parentFolder) {
            // Determine if we need to split
            const splitNode = (desiredParentIdentifier + this.separator).indexOf(parentFolder.data.absoluteIdentifier + this.separator) === -1;

            const parentPosition = arrayToSearch.findIndex(x => {
                const testItemData = ObservableLike.getValue(x.underlyingItem.data);
                return testItemData && testItemData.absoluteIdentifier === parentFolderEx!.underlyingItem.data.absoluteIdentifier;
            });
            const spliceIndex = parentPosition === -1 ? -1 : parentPosition + 1;
            const originalChildCount = this.getVisibleLineage(parentFolderEx).length;

            if (splitNode) {
                // Long path for the new folder to create
                const splitFolderPath = parentFolder.data.absoluteIdentifier.substring(
                    parentFolder.data.absoluteIdentifier.indexOf(searchCommonIdentifier)
                );

                // Rename the common parent to the common identifier
                parentFolder.data.absoluteIdentifier = searchCommonIdentifier;
                parentFolder.data.relativeIdentifier = searchCommonIdentifier.substring(
                    parentFolderEx.parentItem ? parentFolderEx.parentItem.underlyingItem.data.absoluteIdentifier.length + this.separator.length : 0
                );

                // Create the new child folder, set it as a child of the parent, and reassign the children of the old node
                const newFolder: SparseTreeFolder = {
                    kind: "folder",
                    absoluteIdentifier: splitFolderPath,
                    relativeIdentifier: splitFolderPath.substring(searchCommonIdentifier.length + this.separator.length)
                };
                const newNode: ITreeItem<SparseTreeNode<T>> = {
                    data: newFolder,
                    childItems: parentFolder.childItems
                };

                parentFolder.childItems = [];
                this.addChildToInternalStructures(parentFolder, newNode);

                const newFolderExNode = this.convertToExItem(newNode, parentFolderEx);
                this.folders.push(newFolderExNode);
            }

            // If we need to add additional folders to contain this item, add them now
            const additionalFolderPath = desiredParentIdentifier.substr(parentFolder.data.absoluteIdentifier.length);
            if (additionalFolderPath !== "") {
                const newFolder: SparseTreeFolder = {
                    kind: "folder",
                    absoluteIdentifier: desiredParentIdentifier,
                    relativeIdentifier: additionalFolderPath.substring(this.separator.length)
                };
                const newFolderNode: ITreeItem<SparseTreeNode<T>> = { data: newFolder, expanded: false };

                this.addChildToInternalStructures(parentFolder, newFolderNode);

                const newFolderExNode = this.convertToExItem(newFolderNode, parentFolderEx);
                this.folders.push(newFolderExNode);

                parentFolderEx = newFolderExNode;
                parentFolder = newFolderNode;
            }

            this.addChildToInternalStructures(parentFolder, childTreeNode);

            if (searchResult.item && searchResult.item.underlyingItem.expanded) {
                const itemsToSplice = this.getVisibleLineage(searchResult.item);

                return {
                    spliceIndex,
                    removeCount: originalChildCount,
                    itemsToAdd: itemsToSplice,
                    newItemAncestry: this.getAncestry(this.convertToExItem(childTreeNode, parentFolderEx))
                };
            } else {
                return {
                    spliceIndex,
                    removeCount: 0,
                    itemsToAdd: [],
                    newItemAncestry: this.getAncestry(this.convertToExItem(childTreeNode, parentFolderEx))
                };
            }
        } else {
            // If this lives in a folder, make a folder
            // Otherwise, it lives at root, so put it at root
            if (desiredParentIdentifier !== "") {
                // Create a new root node
                const newFolder: SparseTreeFolder = {
                    kind: "folder",
                    absoluteIdentifier: desiredParentIdentifier,
                    relativeIdentifier: desiredParentIdentifier
                };
                const newFolderNode: ITreeItem<SparseTreeNode<T>> = { data: newFolder, childItems: [childTreeNode] };
                const newFolderExNode = this.convertToExItem(newFolderNode);

                this.folders.push(newFolderExNode);
                const spliceIndex = getInsertIndexOfPartitionSortedTreeItems(arrayToSearch, newFolderNode);

                return {
                    spliceIndex,
                    removeCount: 0,
                    itemsToAdd: [newFolderExNode],
                    newItemAncestry: [newFolderExNode]
                };
            } else {
                const spliceIndex = getInsertIndexOfPartitionSortedTreeItems(arrayToSearch, childTreeNode);
                return { spliceIndex, removeCount: 0, itemsToAdd: [this.convertToExItem(childTreeNode)], newItemAncestry: [] };
            }
        }
    };

    /**
     * Clears out all child rows with undefined data, aka loading rows
     */
    private clearLoadingRows = (parentItem: ITreeItem<IObservableLikeValue<SparseTreeNode<T>>>) => {
        if (parentItem.childItems) {
            const sliceLength = parentItem.childItems.length;
            // Remove undefined items
            parentItem.childItems = parentItem.childItems.filter(x => {
                const testItemData = ObservableLike.getValue(x.data);
                return testItemData ? true : false;
            });

            if (parentItem.expanded) {
                const parentItemData = ObservableLike.getValue(parentItem.data);
                const parentPosition = this.value.findIndex(x => {
                    const testItemData = ObservableLike.getValue(x.underlyingItem.data);
                    return testItemData ? testItemData.absoluteIdentifier === parentItemData.absoluteIdentifier : false;
                });
                const parentExItem = this.value[parentPosition];
                this.tableItems.splice(parentPosition + 1, sliceLength, ...parentItem.childItems.map(x => this.convertToExItem(x, parentExItem)));
            }
        }
    };

    private collapse(treeItem: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T>>>) {
        if (!!!treeItem.underlyingItem.expanded) {
            return;
        }

        treeItem.underlyingItem.expanded = false;

        // Remove all visible descendants of this node
        const itemsToRemove = new Set(this.getVisibleLineage(treeItem).map(x => x.underlyingItem));
        this.tableItems.removeAll(x => itemsToRemove.has(x.underlyingItem));
    }

    /**
     * Plenty of type overrides here for safety
     */
    private convertToExItem(convertItem: ITreeItem<SparseTreeNode<T>>, parent?: ITreeItemEx<SparseTreeNode<T>>): ITreeItemEx<SparseTreeNode<T>>;
    private convertToExItem(
        convertItem: ITreeItem<IObservableLikeValue<SparseTreeNode<T>>>,
        parent?: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T>>>
    ): ITreeItemEx<IObservableLikeValue<SparseTreeNode<T>>>;
    private convertToExItem(
        convertItem: ITreeItem<IObservableLikeValue<SparseTreeNode<T> | undefined>>,
        parent?: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>
    ): ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>;
    private convertToExItem(
        convertItem: ITreeItem<IObservableLikeValue<SparseTreeNode<T> | undefined>>,
        parent?: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>
    ): ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>> {
        const convertItemData = ObservableLike.getValue(convertItem.data);
        return {
            depth: parent ? parent.depth + 1 : 0,
            onToggle: convertItemData && convertItemData.kind === "folder" ? this.toggle : undefined,
            parentItem: parent,
            underlyingItem: convertItem
        };
    }

    private createLoadingRow: () => ITreeItem<IObservableLikeValue<undefined>> = () => {
        return {
            data: new ObservableValue(undefined)
        };
    };

    private expand(treeItem: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T>>>) {
        if (this.onFetchChildren) {
            const itemData = ObservableLike.getValue(treeItem.underlyingItem.data);
            const itemIdentifier = itemData.absoluteIdentifier;
            const itemHasMoreData = this.mapItemsToHasMore[itemIdentifier] === undefined ? true : this.mapItemsToHasMore[itemIdentifier];

            if (itemHasMoreData) {
                this.pushChild(treeItem.underlyingItem, this.createLoadingRow());
            }
        }

        const itemData = ObservableLike.getValue(treeItem.underlyingItem.data);
        if (treeItem.underlyingItem.expanded) {
            return;
        }

        // Don't expand if there's no children
        if (treeItem.underlyingItem.childItems) {
            treeItem.underlyingItem.expanded = true;

            // Insert after the item being expanded
            const nodeIndex =
                this.tableItems.value.findIndex(testItem => {
                    const testItemData = ObservableLike.getValue(testItem.underlyingItem.data);
                    return testItemData ? testItemData.absoluteIdentifier === itemData.absoluteIdentifier : false;
                }) + 1;
            const itemsToInsert = this.getVisibleLineage(treeItem);
            this.tableItems.splice(nodeIndex, 0, ...itemsToInsert);
        }
    }

    private findClosestAncestor = (identifier: string, folders: ITreeItemEx<SparseTreeNode<T>>[] = this.folders): SparseTreeSearchResult<T> => {
        let result = undefined;
        let testIdentifier = identifier;
        while (!result && testIdentifier.length > 0) {
            const testIdentifierSegments = testIdentifier.split(this.separator);
            result = folders.find(testFolder => {
                const testFolderSegments = testFolder.underlyingItem.data.absoluteIdentifier.split(this.separator);
                if (testIdentifierSegments.length > testFolderSegments.length) {
                    return false;
                } else {
                    return testFolderSegments
                        .slice(0, testIdentifierSegments.length)
                        .every((testFolderSegment, index) => testFolderSegment === testIdentifierSegments[index]);
                }
            });
            if (!result) {
                testIdentifier = testIdentifier.substring(0, testIdentifier.lastIndexOf(this.separator));
            }
        }

        return { item: result, commonIdentifier: testIdentifier };
    };

    private getAncestry = (item: ITreeItemEx<SparseTreeNode<T>>) => {
        const result: ITreeItemEx<SparseTreeNode<T>>[] = [];
        while (item.parentItem) {
            result.unshift(item.parentItem);
            item = item.parentItem;
        }
        return result;
    };

    private getParentIdentifier = (baseIdentifier: string) => {
        return baseIdentifier.substring(0, baseIdentifier.lastIndexOf(this.separator));
    };

    // Create a flat array of all visible children of the item being expanded
    private getVisibleLineage = (item: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>) => {
        const results: ITreeItemEx<IObservableLikeValue<SparseTreeNode<T> | undefined>>[] = [];

        item.underlyingItem.childItems &&
            item.underlyingItem.childItems.forEach(child => {
                const childAsExNode = this.convertToExItem(child, item);
                results.push(childAsExNode);

                // Only recurse if this node is also expanded
                if (child.expanded) {
                    results.push(...this.getVisibleLineage(childAsExNode));
                }
            });

        return results;
    };

    /**
     * Adds a child item to the END of a node's children; use care, should only be used with loading or loadmore rows
     */
    private pushChild = (
        parentItem: ITreeItem<IObservableLikeValue<SparseTreeNode<T> | undefined>>,
        childItem: ITreeItem<IObservableLikeValue<SparseTreeNode<T> | undefined>>
    ) => {
        const parentData = ObservableLike.getValue(parentItem.data);

        if (!parentData) {
            return;
        }

        const initialChildCount = parentItem.childItems ? parentItem.childItems.length : 0;

        if (parentItem.childItems) {
            parentItem.childItems.push(childItem);
        } else {
            parentItem.childItems = [childItem];
        }

        if (parentItem.expanded) {
            const parentPosition = this.tableItems.value.findIndex(x => {
                const testItemData = ObservableLike.getValue(x.underlyingItem.data);
                return testItemData ? parentData.absoluteIdentifier === testItemData.absoluteIdentifier : undefined;
            });

            if (parentPosition !== -1) {
                const parentItemEx = this.tableItems.value[parentPosition];
                this.tableItems.splice(parentPosition + initialChildCount + 1, 0, this.convertToExItem(childItem, parentItemEx));
            }
        }
    };

    private updateVisibleLineageForFolder = (parentItemEx: ITreeItemEx<SparseTreeFolder>, items: T[]) => {
        const initialVisibleLineageCount = this.getVisibleLineage(parentItemEx).length;
        items.forEach(item => this.addInternal(item));

        const parentPosition = this.value.findIndex(x => {
            const testItemData = ObservableLike.getValue(x.underlyingItem.data);
            return testItemData ? testItemData.absoluteIdentifier === parentItemEx.underlyingItem.data.absoluteIdentifier : false;
        });

        this.tableItems.splice(parentPosition + 1, initialVisibleLineageCount, ...this.getVisibleLineage(parentItemEx));
    };

    private visitContainedItems = (rootNode: ITreeItem<IObservableLikeValue<SparseTreeNode<T> | undefined>>, visitor: (item: T) => T) => {
        rootNode.childItems &&
            rootNode.childItems.forEach((child, index) => {
                const childData = ObservableLike.getValue(child.data);
                if (childData) {
                    if (childData.kind === "item") {
                        childData.data = visitor(childData.data);
                    } else {
                        this.visitContainedItems(child, visitor);
                    }
                }
            });
    };
}
